/**
 * deferred-dom.js
 *
 * Keeps off-screen UI out of the DOM during page load.
 *
 * The mobile nav drawer, search modal, quick-add dialog, pairing prompt and
 * chat drawer are all rendered on every page and all hidden until the shopper
 * opens them. Measured on a product page they account for roughly 735 DOM
 * nodes and 106 KB of HTML that nothing can see. Every one of those nodes is
 * parsed, styled and laid out during the load that decides LCP and TBT.
 *
 * Liquid wraps that markup in <template> (see snippets/deferred-dom.liquid).
 * Content inside a <template> is parsed but never enters the document tree, so
 * it costs no style recalculation, no layout and no memory. This file puts it
 * back.
 *
 * WHY IDLE AND NOT ON CLICK
 * -------------------------
 * Hydrating when the shopper taps the hamburger would be the obvious design and
 * it is the wrong one here. This theme has a documented history of "first tap
 * swallowed" bugs on mobile (see horizon-conversion-investigation.md and the
 * four reverted commits around 9e15845). Anything that makes the first
 * interaction do more work invites that class of defect straight back.
 *
 * So hydration is not lazy, it is *deferred*: it happens as soon as the browser
 * is idle, which is long before a human could plausibly reach for the menu, and
 * well after the critical rendering path is done. By the time any interaction
 * happens the DOM is exactly what it is today.
 *
 * The interaction listeners below are a safety net, not the mechanism. They
 * fire on pointerdown / keydown / focusin in the CAPTURE phase, which runs
 * before any click handler, so even a shopper who taps the instant the page
 * paints gets a fully populated DOM before the drawer's own handler runs.
 *
 * WHY HYDRATION IS NOT ONE-SHOT
 * -----------------------------
 * The header section hydrates itself after load: sections/header.liquid calls
 * hydrate() from section-hydration.js, which re-fetches the header through the
 * Section Rendering API and morphs every [data-hydration-key] subtree into the
 * live DOM (it exists so the mega-menu's featured products/collections, which
 * are skipped on the first render for TTFB, arrive afterwards).
 *
 * `.header__drawer` carries data-hydration-key="header-drawer-mobile", and the
 * Section Rendering response wraps its contents in the very same
 * <template data-deferred-dom> this file exists to unwrap. So the two race:
 *
 *   morph lands first  -> a template is replaced by a template; we unwrap it
 *                         afterwards. Hamburger present, looks fine.
 *   morph lands second -> it replaces the already-unwrapped <header-drawer>
 *                         with an inert <template>. Nothing renders. The
 *                         hamburger is gone for the rest of the page view,
 *                         because the old one-shot `hydrated` latch meant we
 *                         never looked again.
 *
 * The second ordering is the one shoppers hit. It is likeliest on collection
 * pages, where the Section Rendering fetch is slow (Liquid re-renders the whole
 * collection context behind it), and it is close to guaranteed for anyone who
 * touches the screen on load, because that fires the capture-phase listeners
 * above and unwraps at t~0 while the fetch is still in flight.
 *
 * The fix is to stop treating hydration as a one-shot event. Every container we
 * have injected into keeps a childList observer, so if anything ever puts a
 * <template data-deferred-dom> back, we unwrap it again. Re-injecting is safe:
 * <script type="module" src=...> is evaluated once per URL per realm, so
 * recreating it does not re-run the module, and the custom element that module
 * already defined upgrades the new nodes as they are inserted.
 *
 * FAILURE MODE
 * ------------
 * The worst outcome would be UI that never comes back. Every path is wrapped so
 * that any thrown error falls through to a plain, unconditional inject, and a
 * hard timeout hydrates regardless if no other trigger has fired.
 */
(function () {
  var SELECTOR = 'template[data-deferred-dom]';
  var IDLE_TIMEOUT_MS = 2000;
  var HARD_TIMEOUT_MS = 4000;

  /*
   * Backstop against a pathological loop: if something in the page kept putting
   * a template back we would keep unwrapping it forever. A runaway
   * MutationObserver is not a failure mode worth risking on a live storefront,
   * so each container gets a finite number of repairs per window.
   *
   * The window matters. This used to be a lifetime cap, on the assumption that
   * only the once-per-page header morph could ever put a template back. The
   * filters drawer disproved that: every filter change re-rendered its section
   * and put the template back, so the sixth change hit the cap and the drawer
   * stayed empty for the rest of the page view. assets/morph.js now unwraps
   * response templates itself, so repairs should not happen on that path at
   * all — but should any path regress, the cap must throttle a genuine loop
   * (many repairs within the same second) without ever turning legitimate,
   * spaced-out repairs into a blank drawer.
   */
  var MAX_REPAIRS_PER_CONTAINER = 5;
  var REPAIR_WINDOW_MS = 1000;

  /* Set once the first unwrap pass has run. Later passes are "repairs". */
  var hydratedOnce = false;

  /* container -> { count, at }: repairs performed within the current window */
  var repairCounts = new WeakMap();
  /* container -> true once observer.observe() has been called for it */
  var observed = new WeakMap();

  var observer = null;
  var repairScheduled = false;

  /**
   * A <script> cloned out of a <template> is inert by spec — cloning does not
   * mark it "already started", but inserting a parsed-then-cloned script does
   * not run it either. Re-creating the element is the only way to get it to
   * execute, and attributes must be copied so type="module" and src survive.
   */
  function reviveScripts(fragment) {
    var scripts = fragment.querySelectorAll('script');

    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var fresh = document.createElement('script');

      for (var j = 0; j < old.attributes.length; j++) {
        fresh.setAttribute(old.attributes[j].name, old.attributes[j].value);
      }

      if (!old.src) fresh.textContent = old.textContent;

      old.parentNode.replaceChild(fresh, old);
    }
  }

  /**
   * Replaces a <template data-deferred-dom> with its content.
   * @returns {Node|null} the container the content now lives in
   */
  function inject(template, withScripts) {
    var parent = template.parentNode;
    if (!parent) return null;

    var fragment = template.content.cloneNode(true);

    if (withScripts) reviveScripts(fragment);

    parent.replaceChild(fragment, template);

    return parent;
  }

  /**
   * Watch a container for a deferred template reappearing inside it.
   *
   * Scoped to the container and to childList only — no subtree, no
   * document-wide observer — so this stays free on pages with busy DOM
   * (infinite-scroll collections) and does not add to the MutationObserver
   * load discussed in header.js.
   */
  function watch(container) {
    if (!observer || !(container instanceof Element)) return;
    if (observed.get(container)) return;

    observed.set(container, true);
    observer.observe(container, { childList: true });
  }

  /**
   * Re-unwrap anything that has been put back. Runs from the observer, which
   * delivers at the end of the current task, so the repair lands before the
   * next paint and the menu never visibly flickers.
   */
  function repair() {
    repairScheduled = false;

    var templates = document.querySelectorAll(SELECTOR);

    for (var i = 0; i < templates.length; i++) {
      var template = templates[i];
      var parent = template.parentNode;

      if (parent instanceof Element) {
        var now = Date.now();
        var record = repairCounts.get(parent);
        if (!record || now - record.at > REPAIR_WINDOW_MS) {
          record = { count: 0, at: now };
          repairCounts.set(parent, record);
        }
        if (record.count >= MAX_REPAIRS_PER_CONTAINER) continue;
        record.count += 1;
        record.at = now;
      }

      try {
        watch(inject(template, true));
      } catch (error) {
        try {
          watch(inject(template, false));
        } catch (ignored) {
          /* nothing further to try for this one */
        }
      }
    }
  }

  function scheduleRepair() {
    if (repairScheduled) return;
    repairScheduled = true;

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(repair);
    } else {
      window.setTimeout(repair, 0);
    }
  }

  function hydrate() {
    if (hydratedOnce) return;
    hydratedOnce = true;

    var templates = document.querySelectorAll(SELECTOR);

    try {
      for (var i = 0; i < templates.length; i++) watch(inject(templates[i], true));

      document.dispatchEvent(
        new CustomEvent('deferred-dom:hydrated', { detail: { count: templates.length } })
      );
    } catch (error) {
      /*
       * Something went wrong mid-way. Whatever is still a <template> gets
       * injected without the script revival step — a drawer with markup but no
       * behaviour is recoverable, a drawer with no markup is not.
       */
      var remaining = document.querySelectorAll(SELECTOR);

      for (var k = 0; k < remaining.length; k++) {
        try {
          watch(inject(remaining[k], false));
        } catch (ignored) {
          /* nothing further to try for this one */
        }
      }
    }
  }

  /*
   * Created before the first unwrap pass so that pass can register its
   * containers as it goes.
   */
  if (typeof MutationObserver === 'function') {
    observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;

        for (var j = 0; j < added.length; j++) {
          var node = added[j];

          if (
            node.nodeType === 1 &&
            node.nodeName === 'TEMPLATE' &&
            node.hasAttribute('data-deferred-dom')
          ) {
            scheduleRepair();
            return;
          }
        }
      }
    });
  }

  function scheduleIdle() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(hydrate, { timeout: IDLE_TIMEOUT_MS });
    } else {
      window.setTimeout(hydrate, 200);
    }
  }

  /*
   * Capture phase, so this runs before the drawer's own click handler and
   * before any handler bound on an ancestor. once:true keeps it to one call.
   */
  ['pointerdown', 'touchstart', 'keydown', 'focusin'].forEach(function (type) {
    document.addEventListener(type, hydrate, { capture: true, once: true, passive: true });
  });

  if (document.readyState === 'complete') {
    scheduleIdle();
  } else {
    window.addEventListener('load', scheduleIdle, { once: true });
  }

  /* Backstop: hydrate even if load never fires and nobody touches the page. */
  window.setTimeout(hydrate, HARD_TIMEOUT_MS);
})();
